type FontProps = 'circular' | 'camphor';

const getFontClass = (font: FontProps) => {
  const fonts = {
    circular: 'font-circular',
    camphor: 'font-camphor'
  };

  return fonts[font];
};

type WeightProps = 400 | 500 | 600 | 700 | 900;

const getFontWeightClass = (weight: WeightProps) => {
  const fontWeights = {
    100: 'font-thin',
    200: 'font-extralight',
    300: 'font-light',
    400: 'font-normal',
    500: 'font-medium',
    600: 'font-semibold',
    700: 'font-bold',
    800: 'font-extrabold',
    900: 'font-black'
  };

  return fontWeights[weight];
};

type TitleSizeProps =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6';

const getTitleSizeClass = (size: TitleSizeProps) => {
  const sizes = {
    h1: 'text-4xl md:text-5xl lg:text-7xl',
    h2: 'text-3xl md:text-4xl lg:text-5xl',
    h3: 'text-2xl md:text-3xl lg:text-4xl',
    h4: 'text-xl md:text-2xl lg:text-3xl',
    h5: 'text-lg md:text-xl lg:text-2xl',
    h6: 'text-base md:text-lg lg:text-xl'
  };

  return sizes[size];
};

type ColorProps =
  | 'blue'
  | 'blue-dark'
  | 'yellow'
  | 'indigo'
  | 'gray'
  | 'white';

const getColorClass = (color: ColorProps) => {
  const colors = {
    blue: 'text-sl-blue',
    'blue-dark': 'text-sl-blue-dark',
    yellow: 'text-sl-yellow',
    indigo: 'text-sl-indigo',
    gray: 'text-sl-gray',
    white: 'text-white'
  };

  return colors[color];
};

type TextSizeProps = 'xs' | 'sm' | 'base' | 'lg';

const getTextSizeClass = (size: TextSizeProps) => {
  const sizes = {
    xs: 'text-xs md:text-sm',
    sm: 'text-sm md:text-base lg:text-lg',
    base: 'text-base md:text-lg lg:text-xl',
    lg: 'text-lg md:text-xl lg:text-2xl'
  };

  return sizes[size];
};

type TextTrackingProps =
  | 'tighter'
  | 'tight'
  | 'normal'
  | 'wide'
  | 'wider'
  | 'widest';

const getTrackingClass = (value: TextTrackingProps) => {
  const tracking = {
    tighter: 'tracking-tighter',
    tight: 'tracking-tight',
    normal: 'tracking-normal',
    wide: 'tracking-wide',
    wider: 'tracking-wider',
    widest: 'tracking-widest'
  };

  return tracking[value];
};

type TextLeadingProps =
  | 'none'
  | 'tight'
  | 'snug'
  | 'normal'
  | 'relaxed'
  | 'loose';

const getLeadingClass = (value: TextLeadingProps) => {
  const leading = {
    none: 'leading-none md:leading-none lg:leading-none',
    tight:
      'leading-tight md:leading-tight lg:leading-tight',
    snug: 'leading-snug md:leading-snug lg:leading-snug',
    normal:
      'leading-normal md:leading-normal lg:leading-normal',
    relaxed:
      'leading-relaxed md:leading-relaxed lg:leading-relaxed',
    loose: 'leading-loose md:leading-loose lg:leading-loose'
  };

  return leading[value];
};

const getAttentionColorName = (color: string): string => {
  // contrast colors according to original color
  switch (color) {
    case 'blue':
    case 'blue-dark':
      return 'indigo';

    case 'gray':
    case 'indigo':
    case 'white':
      return 'yellow';

    case 'yellow':
      return 'indigo';
    default:
      return '';
  }
};

export {
  getFontClass,
  getFontWeightClass,
  getTitleSizeClass,
  getColorClass,
  getTextSizeClass,
  getTrackingClass,
  getLeadingClass,
  getAttentionColorName
};

export type {
  TextSizeProps,
  TitleSizeProps,
  FontProps,
  ColorProps,
  WeightProps,
  TextTrackingProps,
  TextLeadingProps
};
